import { CHANNEL_ID } from '@@types/common';
import Log from '@@types/Log';

import { LOG_SEARCH_KEYWORD_MAX_LEN, NOTIFICATION, PANEL_TYPE } from '~/constants';
import i18n from '~/i18n';
import { isDirectChannel, validateDescription, validateTitle } from '~/services/channelService';
import popupWindowService from '~/services/popupWindowService';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { doorayAppStore } from '~/stores/DoorayAppStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { membersStore } from '~/stores/MembersStore';
import { searchStore } from '~/stores/SearchStore';
import { uiState } from '~/stores/UiState';
import {
  createEphemeralLog,
  extractMentionMembersId,
  extractMentionTypeAndId,
  extractParamStr,
  extractPopupParams,
  extractStatus,
  validateEmpty,
} from '~/utils/channelLogsUtil';
import rename from '~/utils/fnUtil/rename';
import { mEither, mMaybe } from '~/utils/monadUtil';
import { replaceMentionToName, validateStatus } from '~/utils/textUtil/textUtil';
import { isEmpty } from 'lodash';
import { runInAction } from 'mobx';
import {
  __,
  append,
  cond,
  curry,
  evolve,
  ifElse,
  includes,
  juxt,
  map,
  mergeRight,
  objOf,
  pipe,
  prop,
  propEq,
  reject,
  replace,
  trim,
} from 'ramda';

import Actions from '../Actions';
import { stores } from '../Stores';

class DoorayCommandActions {
  private appendEphemeralLog = curry((channelId: string, text: string) => {
    const seq = channelsStore.getById(channelId).seq;
    const ephemeralLog = createEphemeralLog({
      channelId,
      text,
      customName: doorayAppStore.name,
      senderId: doorayAppStore.id,
      seq,
    });
    juxt([mainDetailUiState.setScrollTargetLog, channelLogsStore.setLog])(ephemeralLog as Log);
  });

  constructor(private actions: Actions) {}

  private handleTopic = ({ channelId, name, text }) => {
    pipe<any, any, any, any, any>(
      extractParamStr,
      validateTitle,
      map(objOf('title')),
      mEither(
        () => this.appendErrorLog(channelId, name),
        this.actions.channelActions.updateTitleAndDescription(channelId)
      )
    )(text);
  };

  private handleDesc = ({ channelId, name, text }) => {
    pipe<any, any, any, any, any>(
      extractParamStr,
      validateDescription,
      map(objOf('description')),
      mEither(
        () => this.appendErrorLog(channelId, name),
        this.actions.channelActions.updateTitleAndDescription(channelId)
      )
    )(text);
  };

  private getMemberMail = (memberId) => {
    const member = searchStore.getSearchedMemberById(memberId) || interTenantMembersStore.getById(memberId);
    return member.emailAddress;
  };

  private openDoorayPopupByType = (type, text) => {
    pipe<any, any, any, any, any, any>(
      trim,
      extractPopupParams,
      evolve({ membersId: map(this.getMemberMail) }),
      rename({ membersId: 'emailAddress' }),
      ({ emailAddress, title }: { emailAddress: string[]; title: string }) =>
        popupWindowService.openDoorayFeaturePopup(type, emailAddress, title)
    )(text);
  };

  private handleSchedulePopup = curry((channelId: CHANNEL_ID, { text }) => {
    if (text.trim() !== '/event') {
      this.openDoorayPopupByType('calendar', text);
      return;
    }
    const channel = channelsStore.getById(channelId);
    if (channel && channel.members?.length > 0) {
      const memberMails = channel.members
        .map((memberId) => membersStore.getById(memberId)?.emailAddress)
        .filter((value) => !!value);
      popupWindowService.openCalendar(memberMails);
    }
  });

  private handlePopup = curry((type, { text }) => {
    this.openDoorayPopupByType(type, text);
  });

  private handleMute = ({ channelId }) => {
    this.actions.channelActions.updateNotification(channelId, NOTIFICATION.OFF);
  };

  private handleUnmute = ({ channelId }) => {
    this.actions.channelActions.updateNotification(channelId, NOTIFICATION.ON);
  };

  private handleInvite = ({ channelId, name, text }) => {
    const channel = channelsStore.getById(channelId);

    pipe(
      extractMentionMembersId,
      reject(includes(__, channel.members)),
      validateEmpty,
      mEither(
        () => this.appendErrorLog(channelId, name),
        ifElse<any, any, any>(
          () => isDirectChannel(channel),
          pipe<any, any, any, any>(
            append(channel.opponentId),
            objOf('memberIds'),
            this.actions.channelActions.createGroupChannel
          ),
          pipe(objOf('members'), this.actions.modalActions.openChannelInviteCommand)
        )
      )
    )(text);
  };

  private handleLeave = ({ channelId }) => {
    this.actions.modalActions.openChannelLeave(channelId);
  };

  private handleSearch = ({ text }) => {
    const keyword = extractParamStr(text);
    if (keyword.length > LOG_SEARCH_KEYWORD_MAX_LEN) {
      return this.actions.notificationActions.showError({
        text: i18n.t('limitKeywordLength', {
          defaultValue: '최대 {{max}}글자까지 입력 가능합니다.',
          max: LOG_SEARCH_KEYWORD_MAX_LEN,
        }),
      });
    }
    runInAction(() => {
      uiState.setPanelType(PANEL_TYPE.LOG);
      if (isEmpty(keyword)) return;
      searchStore.setSearchParams(null);
      searchStore.setChannelId(mainDetailUiState.contentsChannelId);
      pipe(replaceMentionToName, replace('@', ''), searchStore.setSearchKeyword)(keyword);
      this.actions.searchActions.searchLogs();
    });
  };

  private handleGoto = ({ channelId, name, text }) => {
    pipe(
      extractMentionTypeAndId,
      validateEmpty,
      mEither(
        () => this.appendErrorLog(channelId, name),
        ({ id, type }) => {
          if (type === 'channel') {
            this.actions.channelActions.selectChannel(id);
          } else {
            this.actions.channelActions.selectOrCreateDirectChannelByOpponentId(id);
          }
        }
      )
    )(text);
  };

  private handleStatus = ({ name, text, channelId }) => {
    pipe(
      extractStatus,
      validateStatus,
      mEither(() => this.appendErrorLog(channelId, name), this.actions.proxyActions.updateMeStatus)
    )(text);
  };

  executeCommand = curry((channelId, params) => {
    cond([
      [propEq('name', '/topic'), this.handleTopic],
      [propEq('name', '/desc'), this.handleDesc],
      [propEq('name', '/task'), this.handlePopup('task')],
      [propEq('name', '/event'), this.handleSchedulePopup(channelId)],
      [propEq('name', '/mail'), this.handlePopup('mail')],
      [propEq('name', '/form'), this.handlePopup('form')],
      [propEq('name', '/mute'), this.handleMute],
      [propEq('name', '/unmute'), this.handleUnmute],
      [propEq('name', '/invite'), this.handleInvite],
      [propEq('name', '/leave'), this.handleLeave],
      [propEq('name', '/search'), this.handleSearch],
      [propEq('name', '/goto'), this.handleGoto],
      [propEq('name', '/status'), this.handleStatus],
    ])(mergeRight({ channelId }, params));
  });

  private getErrorMessage = (commandName: string) => {
    return mMaybe('', prop('errorMessage'), stores.safeGetDoorayCommand(commandName));
  };

  private appendErrorLog = curry((channelId: string, commandName: string) => {
    pipe(this.getErrorMessage, this.appendEphemeralLog(channelId))(commandName);
  });
}

export default DoorayCommandActions;
